import React, { useCallback } from 'react'
import * as Sentry from '@sentry/react'
import './ExternalUpdates.scss'
import { Col, Container, Row } from 'react-bootstrap'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { formatDate } from '../../common/aifeatures/utils'
import { useAppDispatch, useAppSelector } from '../../../../../store/hooks'
import { useSyncStatuses } from './hooks'
import { SyncStatusView } from './models'
import { selectCurrentUser } from '../../../../../store/meetingSummary/user'
import { LinkButton } from '../../../../../components/Button/LinkButton'
import { Button } from '../../../../../components/Button/Button'
import { getExternalLink } from './utils'
import { ExternalUpdateTitle } from './ExternalUpdateTitle'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { useTracking } from '../../common/tracking/hooks'
import { TrackEvent } from '../../common/tracking'
import { setHistoryContactValues, selectContactChanges } from '../../../../../store/meetingSummary/contactUpdate'
import { hideSidebar } from '../../../../../store/meetingSummary/display'
import { User } from '../../../../../models/User'

export const ExternalUpdates: React.FC = () => {
  const dispatch = useAppDispatch()

  const currentUser = useAppSelector(selectCurrentUser)

  const { syncStatuses } = useSyncStatuses()

  const handleHideSidebar = () => {
    dispatch(hideSidebar())
  }

  return (
    <div className='d-flex flex-column gap-2 zocks-py-1.25 zocks-px-1.25 h-100'>
      {syncStatuses.length === 0 ? (
        <div className='d-flex flex-column h-100 justify-content-center align-items-center'>
          <h4>No updates yet</h4>
          <div className='text-center'>
            <div>The complete update history will appear here with direct access links. Meeting summaries, tasks, notes, and more.</div>
            <div>All in one place.</div>
          </div>
        </div>
      ) : null}
      {syncStatuses.map((syncStatus) => (
        <div key={`${syncStatus.serviceId}-${syncStatus.externalId}-${syncStatus.syncedAt}`}>
          <SyncStatusItem currentUser={currentUser} syncStatus={syncStatus} source='history' onHideSidebar={handleHideSidebar} />
        </div>
      ))}
    </div>
  )
}

interface SyncStatusItemProps {
  currentUser: User | null
  syncStatus: SyncStatusView
  showStartLine?: boolean
  showEndLine?: boolean
  source: 'workflow' | 'history'
  onHideSidebar: () => void
}

export const SyncStatusItem: React.FC<SyncStatusItemProps> = ({
  currentUser,
  syncStatus,
  showStartLine = false,
  showEndLine = false,
  source,
  onHideSidebar,
}) => {
  const contactChanges = useAppSelector(selectContactChanges)
  const dispatch = useAppDispatch()

  const { trackingClient } = useTracking()

  const onClickOpenExternalLink = useCallback(
    () =>
      trackingClient.track({
        eventName: TrackEvent.CRM_LINK_CLICKED,
        eventDetails: {
          serviceId: syncStatus.serviceId,
          source: source,
        },
      }),
    [trackingClient, syncStatus.serviceId, source],
  )

  const onClickViewDetails = useCallback(() => {
    if (!syncStatus.metadata?.contact_values) {
      return
    }

    try {
      const contactValues: unknown =
        typeof syncStatus.metadata.contact_values === 'string' ? JSON.parse(syncStatus.metadata.contact_values) : syncStatus.metadata.contact_values

      if (!contactValues || typeof contactValues !== 'object') {
        console.error('Invalid contact_values format:', contactValues)
        return
      }

      const contactChangeId = syncStatus.metadata.contactChangeId
      const contactChange = contactChangeId && contactChanges ? contactChanges.find((c) => c.id === contactChangeId) : null
      const contactSchema = contactChange?.contactSchema

      trackingClient.track({
        eventName: TrackEvent.CONTACT_UPDATE_CHANGELOG_OPENED,
        eventDetails: {
          serviceId: syncStatus.serviceId,
        },
      })

      dispatch(
        setHistoryContactValues({
          contactValues: contactValues as Record<string, unknown>,
          serviceId: syncStatus.serviceId,
          contactSchema,
          syncStatus,
        }),
      )

      onHideSidebar()
    } catch (error) {
      Sentry.captureException(error)
      console.error('Failed to parse contact_values from metadata:', error)
    }
  }, [contactChanges, dispatch, onHideSidebar, syncStatus, trackingClient])

  const externalLink = getExternalLink({ syncStatus, currentUser })
  const hasContactValues = !!syncStatus.metadata?.contact_values

  return (
    <Container className=''>
      {showStartLine ? (
        <Row>
          <Col xs='auto'>
            <div className='d-flex justify-content-center external-updates-vertical-line--fixed-size my-1'>
              <div className='external-updates-vertical-line' />
            </div>
          </Col>
        </Row>
      ) : null}
      <Row className='py-3 sync-status-item zocks-border-0.5'>
        <Col xs='auto' className='d-flex align-items-center justify-content-center flex-column'>
          <div className='mb-2'>
            <IntegrationIcon serviceId={syncStatus.serviceId as IntegrationServiceId} width={'1rem'} height={'1rem'} />
          </div>
          <div className='external-updates-vertical-line' />
        </Col>
        <Col>
          <div>
            <ExternalUpdateTitle syncStatus={syncStatus} />
            <div>{formatDate(syncStatus.syncedAt)}</div>
            <div className='mt-3 d-flex gap-3'>
              {!!externalLink && (
                <LinkButton
                  onClick={onClickOpenExternalLink}
                  variant='link'
                  className='sync-status-item-external-link button-medium m-0 justify-content-start'
                  relative='route'
                  target='_blank'
                  to={externalLink}
                >
                  <i className='bi bi-box-arrow-up-right' />
                  <span>Open in {getIntegrationDisplayName(syncStatus.serviceId)}</span>
                </LinkButton>
              )}
              {hasContactValues && (
                <Button onClick={onClickViewDetails} variant='link' className='sync-status-item-external-link button-medium m-0 justify-content-start'>
                  <i className='bi bi-eye' />
                  <span>View details</span>
                </Button>
              )}
            </div>
          </div>
        </Col>
      </Row>
      {showEndLine ? (
        <Row>
          <Col xs='auto'>
            <div className='d-flex justify-content-center external-updates-vertical-line--fixed-size my-1'>
              <div className='external-updates-vertical-line' />
            </div>
          </Col>
        </Row>
      ) : null}
    </Container>
  )
}
